import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { ISelectFormFieldProps } from '@components/dynamic-form/types/inputTypes';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { trimTrailingZeros } from '@utils/formatting-utils';
import { enumListAsRecordObject } from '@utils/graph-utils';

import { cloneDeep, isEmpty } from 'lodash';
import DateService from 'src/core/services/DateService';
import PrecisionService from 'src/core/services/PrecisionService';
import {
  IBaseDrawerProps,
  ISalesforceBusinessPartnerCommissionInfo
} from '@common/types/drawer.types';
import {
  useGetPlansCommissionLovs,
  useSetPlansCommission
} from '@common/slices';
import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import { useSelector } from 'react-redux';
import { RootState } from '@core/redux/store';
import { setPlansCommissionDrawerSections } from '@common/constants';

interface Props extends IBaseDrawerProps {
  businessPartnerId: string;
  selectedPlans: string[];
  selectedPlansCodes: string[];

  onSuccess: () => void;
  defaultCommissionType: 'RATE' | 'AMOUNT';
  defaultCommissionRate: number;
  defaultCommissionAmount: number;
  defaultCommissionCurrency: string;
}

const SetPLansCommissionDrawer: React.FC<Props> = ({
  businessPartnerId,
  selectedPlans,
  selectedPlansCodes,
  onSuccess,
  defaultCommissionRate,
  defaultCommissionType,
  defaultCommissionAmount,
  defaultCommissionCurrency
}) => {
  const submitButtonState = useSelector(
    (state: RootState) => state.apiState.buttonState
  );
  const { data } = useGetPlansCommissionLovs.useQuery({
    options: {
      variables: {
        businessPartnerPlanID: selectedPlans[0],
        includeDetails: selectedPlans.length === 1
      }
    }
  });
  const [setPlansCommission] = useSetPlansCommission();
  const [inputsForm, setInputsForm] = useState<IDynamicFormWidgetSection[]>(
    setPlansCommissionDrawerSections
  );

  const loadLovs = async () => {
    const commissionTypes = enumListAsRecordObject(
      data?.Core_CommissionTypes?.enumValues || []
    );

    const currencies: Record<string, string> = {};
    (data?.core?.getCurrencies || []).forEach((c: any) => {
      currencies[c.id] = c.title;
    });

    const applicableOn: Record<string, string> = {};
    (data?.planConfig?.getCommissionApplicableOnOptions || []).forEach(
      (o: any) => {
        applicableOn[o.id] = o.title;
      }
    );

    return { commissionTypes, currencies, applicableOn };
  };
  const buildDefaultEntity = (
    pd: any = null
  ): ISalesforceBusinessPartnerCommissionInfo => {
    const many = selectedPlans.length > 1;

    const plan = pd?.plan ?? {};
    const line = pd?.businessPartnerLine ?? {};

    let commissionType = defaultCommissionType || 'RATE';
    if (!many) {
      commissionType =
        pd?.commissionType ??
        (plan.commissionApplicable ? plan.commissionType : undefined) ??
        line.commissionType ??
        defaultCommissionType ??
        'RATE';
    }

    let commissionRate = defaultCommissionRate || 0;
    if (!many) {
      commissionRate =
        pd?.commissionRate ??
        (plan.commissionApplicable ? plan.commissionRate : undefined) ??
        line.commissionRate ??
        defaultCommissionRate ??
        0;
    }
    commissionRate = PrecisionService.multiplyBy100(commissionRate);

    let commissionAmount = defaultCommissionAmount || 0;
    let commissionCurrency = defaultCommissionCurrency || '39735';
    if (!many) {
      commissionAmount =
        pd?.commissionAmount ??
        (plan.commissionApplicable ? plan.commissionAmount : undefined) ??
        line.commissionAmount ??
        defaultCommissionAmount ??
        0;

      commissionCurrency =
        pd?.commissionAmountCurrency ??
        (plan.commissionApplicable
          ? plan.commissionAmountCurrency
          : undefined) ??
        line.commissionAmountCurrency ??
        defaultCommissionCurrency ??
        '39735';
    }

    let applicableOn = '';
    if (!many) {
      applicableOn =
        pd?.commissionApplicableOn ??
        (plan.commissionApplicable ? plan.commissionApplicableOn : undefined) ??
        line.commissionApplicableOn ??
        '';
    }

    const activeFrom = !many ? (pd?.activeFrom ?? '') : '';
    const activeTo = !many ? (pd?.activeTo ?? '') : '';

    return {
      assignedLineId: '',
      commissionType: commissionType.toUpperCase(),
      lineCommissionValue: String(trimTrailingZeros(commissionAmount)),
      lineCommissionValueCurrency: commissionCurrency,
      lineCommissionRate: String(trimTrailingZeros(commissionRate)),
      lineCommissionApplicableOn: applicableOn,
      activeFrom,
      activeTo
    };
  };

  const initialise = async () => {
    try {
      let details: any = null;

      if (selectedPlans.length === 1) {
        details = data?.salesforce?.getBusinessPartnerPlanDetails ?? null;
      }
      const lovs = await loadLovs();

      const updatedSections = cloneDeep(inputsForm);
      const inputs = updatedSections[0].inputs;

      // Setup selectedPlans field
      const planOptions: Record<string, string> = {};
      const selectedPlanIds: string[] = [];

      selectedPlansCodes.forEach((planCode, index) => {
        const planId = selectedPlans[index] || index.toString();

        planOptions[planId] = planCode;
        selectedPlanIds.push(planId);
      });

      (inputs.selectedPlans as ISelectFormFieldProps).selectOptions =
        planOptions;
      inputs.selectedPlans.value = selectedPlanIds;

      (inputs.commissionType as ISelectFormFieldProps).selectOptions =
        lovs.commissionTypes;
      (
        inputs.lineCommissionApplicableOn as ISelectFormFieldProps
      ).selectOptions = lovs.applicableOn;
      (
        inputs.lineCommissionValueCurrency as ISelectFormFieldProps
      ).selectOptions = lovs.currencies;

      const entity = buildDefaultEntity(details);

      inputs.commissionType.value = entity.commissionType;
      inputs.lineCommissionValue.value = entity.lineCommissionValue;
      inputs.lineCommissionValueCurrency.value =
        entity.lineCommissionValueCurrency;
      inputs.lineCommissionRate.value = entity.lineCommissionRate;
      ((inputs.lineCommissionApplicableOn.value = Object.keys(
        lovs.applicableOn
      ).find((key) => lovs.applicableOn[key] === 'Total Premium')),
        (inputs.activeFrom.value = entity.activeFrom));
      inputs.activeTo.value = entity.activeTo;

      setInputsForm(updatedSections);
    } catch (err) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  useEffect(() => {
    initialise();
  }, [data]);

  const submitForm = async (values: Record<string, any>) => {
    const [data] = normalizeDynamicFormDrawerValues(
      setPlansCommissionDrawerSections,
      values
    );

    const isRate = data.commissionType.toUpperCase() === 'RATE';

    const input = {
      businessPartnerId,
      businessPartnerPlanIds: selectedPlans,
      commissionActiveFrom: DateService.formatDateBackend(data.activeFrom),
      commissionActiveTo: DateService.formatDateBackend(data.activeTo),
      commissionType: data.commissionType.toUpperCase(),
      commissionRate: isRate
        ? PrecisionService.divideBy100(data.lineCommissionRate)
        : null,
      commissionAmount: !isRate ? Number(data.lineCommissionValue) : null,
      commissionCurrency: !isRate ? data.lineCommissionValueCurrency : null,
      commissionApplicableOn: isRate
        ? data.lineCommissionApplicableOn || null
        : null
    };

    const res = await setPlansCommission({ input });

    if (isEmpty(res.errors)) {
      onSuccess();
    }
  };

  return (
    <DynamicFormWidget
      sections={inputsForm}
      onSubmit={submitForm}
      submitButtonState={submitButtonState}
    />
  );
};

export { SetPLansCommissionDrawer };
